import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  RefreshCw, TrendingUp, AlertCircle, Check, Plus, Edit2, Trash2,
  Search, X, Loader, Store, Users, Package, DollarSign,
  Calendar, CheckCircle, XCircle, Shield, Layers
} from 'lucide-react';
import moment from 'moment';
import 'moment/locale/fr';
import './SaaSManagement.css';

moment.locale('fr');

const PLAN_COLORS = {
  starter:    { bg: '#ecfdf5', color: '#16a34a', border: '#bbf7d0', label: 'Starter' },
  pro:        { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', label: 'Pro' },
  entreprise: { bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff', label: 'Entreprise' },
};

const fmt = (n) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'XOF', minimumFractionDigits: 0
}).format(n || 0);

function getAboStatus(dateFin) {
  if (!dateFin) return { label: 'N/A', type: 'none' };
  const diff = Math.ceil((new Date(dateFin) - new Date()) / 86400000);
  if (diff < 0)  return { label: 'Expiré',           type: 'expired' };
  if (diff < 7)  return { label: `${diff}j restants`, type: 'warning' };
  return             { label: 'Actif',            type: 'active' };
}

/* ── Modal base ── */
function Modal({ title, subtitle, onClose, children }) {
  return (
    <>
      <div className="sm-overlay" onClick={onClose}/>
      <div className="sm-modal">
        <div className="sm-modal-head">
          <div>
            <h2 className="sm-modal-title">{title}</h2>
            {subtitle && <p className="sm-modal-sub">{subtitle}</p>}
          </div>
          <button className="sm-modal-close" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="sm-modal-body">{children}</div>
      </div>
    </>
  );
}

export default function SaaSManagement() {
  const navigate = useNavigate();

  const [entreprises, setEntreprises]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filterPlan, setFilterPlan]     = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Modal states
  const [renewalTarget,  setRenewalTarget]  = useState(null);
  const [planTarget,     setPlanTarget]     = useState(null);
  const [showAddModal,   setShowAddModal]   = useState(false);
  const [editTarget,     setEditTarget]     = useState(null);

  const [renewalForm, setRenewalForm] = useState({ moisSupplementaires: 1, raison: '' });
  const [planForm,    setPlanForm]    = useState({ nouveauPlan: '', raison: '' });
  const [addForm,     setAddForm]     = useState({ nom:'', email:'', password:'', plan:'starter', prenom:'Admin', nomAdmin:'Principal' });
  const [editForm,    setEditForm]    = useState({ nom:'', ninea:'', rc:'', telephone:'', adresse:'' });
  const [submitting,  setSubmitting]  = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await axios.get('/api/super-admin/entreprises');
      setEntreprises(data.data || []);
    } catch { alert('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const handleRenewal = async (e) => {
    e.preventDefault();
    if (!renewalForm.raison.trim()) { alert('Raison requise'); return; }
    setSubmitting(true);
    try {
      await axios.post(`/api/super-admin/abonnements/${renewalTarget.abonnement._id}/renouveler`, renewalForm);
      setRenewalTarget(null); load();
    } catch (err) { alert(err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handlePlanChange = async (e) => {
    e.preventDefault();
    if (!planForm.raison.trim()) { alert('Raison requise'); return; }
    if (planForm.nouveauPlan === planTarget.abonnement.plan) { alert('Plan identique'); return; }
    setSubmitting(true);
    try {
      await axios.put(`/api/super-admin/abonnements/${planTarget.abonnement._id}/plan`, planForm);
      setPlanTarget(null); load();
    } catch (err) { alert(err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post('/api/super-admin/entreprises', addForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setShowAddModal(false);
      setAddForm({ nom:'', email:'', password:'', plan:'starter', prenom:'Admin', nomAdmin:'Principal' });
      load();
    } catch (err) { alert(err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.put(`/api/super-admin/entreprises/${editTarget._id}`, editForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEditTarget(null); load();
    } catch (err) { alert(err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (ent) => {
    if (!window.confirm(`Supprimer définitivement "${ent.nom}" et toutes ses données ?`)) return;
    try {
      await axios.delete(`/api/super-admin/entreprises/${ent._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      load();
    } catch { alert('Erreur lors de la suppression'); }
  };

  const filtered = entreprises.filter(e => {
    const q = search.toLowerCase();
    if (q && !e.nom?.toLowerCase().includes(q) && !e.email?.toLowerCase().includes(q)) return false;
    if (filterPlan !== 'all' && e.abonnement?.plan !== filterPlan) return false;
    if (filterStatus === 'actif'   && !e.actif) return false;
    if (filterStatus === 'inactif' && e.actif)  return false;
    if (filterStatus === 'expire'  && e.abonnement && getAboStatus(e.abonnement.dateFin).type !== 'expired') return false;
    return true;
  });

  /* KPI summary */
  const nbActives  = entreprises.filter(e => e.actif).length;
  const nbExpires  = entreprises.filter(e => e.abonnement && getAboStatus(e.abonnement.dateFin).type === 'expired').length;
  const caAttendu  = entreprises.filter(e => e.actif && e.abonnement).reduce((s, e) => s + (e.abonnement.prix || 0), 0);

  if (loading) return (
    <div className="sm-loading"><Loader className="sm-spinner" size={28}/><span>Chargement…</span></div>
  );

  return (
    <div className="sm-page">

      {/* ── Header ── */}
      <div className="sm-header">
        <div className="sm-header-left">
          <div className="sm-header-icon"><Shield size={20}/></div>
          <div>
            <p className="sm-header-date">{moment().format('dddd D MMMM YYYY')}</p>
            <h1 className="sm-header-title">Gestion des Abonnements</h1>
            <p className="sm-header-sub">Renouvellements · Changements de plans · Entreprises</p>
          </div>
        </div>
        <div className="sm-header-right">
          <button className="sm-nav-btn" onClick={() => navigate('/super-admin')}>
            <Shield size={13}/> Dashboard
          </button>
          <button className="sm-nav-btn" onClick={() => navigate('/plans-management')}>
            <Layers size={13}/> Plans
          </button>
          <button className="sm-cta-btn" onClick={() => setShowAddModal(true)}>
            <Plus size={14}/> Nouvelle entreprise
          </button>
        </div>
      </div>

      <div className="sm-content">

        {/* KPI row */}
        <div className="sm-kpi-row">
          {[
            { label: 'Total entreprises', value: entreprises.length, icon: Store,    color: '#6366f1', bg: '#eef2ff' },
            { label: 'Actives',           value: nbActives,           icon: CheckCircle, color: '#10b981', bg: '#ecfdf5' },
            { label: 'Expirées',          value: nbExpires,           icon: XCircle,  color: '#ef4444', bg: '#fef2f2' },
            { label: 'CA mensuel attendu', value: fmt(caAttendu),     icon: DollarSign, color: '#fca311', bg: '#fff8ec' },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="sm-kpi" style={{ borderTopColor: k.color }}>
                <div className="sm-kpi-top">
                  <div className="sm-kpi-icon" style={{ background: k.bg, color: k.color }}>
                    <Icon size={18}/>
                  </div>
                </div>
                <div className="sm-kpi-val">{k.value}</div>
                <div className="sm-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="sm-filters">
          <div className="sm-search-wrap">
            <Search size={14} className="sm-search-icon"/>
            <input className="sm-search" placeholder="Rechercher une entreprise…"
              value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
          <select className="sm-select" value={filterPlan} onChange={e => setFilterPlan(e.target.value)}>
            <option value="all">Tous les plans</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="entreprise">Entreprise</option>
          </select>
          <select className="sm-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">Tous statuts</option>
            <option value="actif">Actifs</option>
            <option value="inactif">Inactifs</option>
            <option value="expire">Expirés</option>
          </select>
          <span className="sm-count">{filtered.length} résultat{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        <div className="sm-card sm-card--table">
          <table className="sm-table">
            <thead>
              <tr>
                <th>Entreprise</th>
                <th>Plan</th>
                <th>Abonnement</th>
                <th>Limites</th>
                <th>Utilisation</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="7" className="sm-empty-row">Aucune entreprise trouvée</td></tr>
              ) : filtered.map(ent => {
                const pl  = PLAN_COLORS[ent.abonnement?.plan] || { bg:'#f8fafc', color:'#64748b', label:'N/A' };
                const abo = getAboStatus(ent.abonnement?.dateFin);
                return (
                  <tr key={ent._id} className={!ent.actif ? 'sm-row--dim' : ''}>
                    <td>
                      <div className="sm-ent-cell">
                        <div className="sm-ent-av" style={{ background: pl.bg, color: pl.color }}>
                          {ent.nom?.charAt(0)?.toUpperCase() || 'E'}
                        </div>
                        <div>
                          <div className="sm-ent-name">{ent.nom}</div>
                          <div className="sm-ent-email">{ent.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="sm-plan-pill" style={{ background: pl.bg, color: pl.color }}>
                        {pl.label}
                      </span>
                      {ent.abonnement?.prix && (
                        <div className="sm-plan-price">{fmt(ent.abonnement.prix)}/mois</div>
                      )}
                    </td>
                    <td>
                      <span className={`sm-abo-badge sm-abo-${abo.type}`}>{abo.label}</span>
                      {ent.abonnement?.dateFin && (
                        <div className="sm-abo-date">
                          {moment(ent.abonnement.dateFin).format('DD/MM/YYYY')}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="sm-limits">
                        <span><Store size={11}/> {ent.abonnement?.limites?.boutiques || '∞'}</span>
                        <span><Users size={11}/> {ent.abonnement?.limites?.utilisateurs || '∞'}</span>
                        <span><Package size={11}/> {ent.abonnement?.limites?.produits || '∞'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="sm-usage">
                        <span className="sm-usage-item">{ent.stats?.boutiques || 0} bts</span>
                        <span className="sm-usage-item">{ent.stats?.utilisateurs || 0} users</span>
                        <span className="sm-usage-item">{ent.stats?.produits || 0} produits</span>
                      </div>
                    </td>
                    <td>
                      <span className={`sm-status-badge ${ent.actif ? 'active' : 'inactive'}`}>
                        {ent.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td>
                      <div className="sm-actions">
                        <button className="sm-action-btn sm-action-btn--primary"
                          disabled={!ent.actif || !ent.abonnement}
                          onClick={() => { setRenewalTarget(ent); setRenewalForm({ moisSupplementaires:1, raison:'' }); }}
                          title="Renouveler">
                          <RefreshCw size={13}/> Renouveler
                        </button>
                        <button className="sm-action-btn sm-action-btn--secondary"
                          disabled={!ent.actif || !ent.abonnement}
                          onClick={() => { setPlanTarget(ent); setPlanForm({ nouveauPlan: ent.abonnement?.plan || '', raison:'' }); }}
                          title="Changer plan">
                          <TrendingUp size={13}/> Plan
                        </button>
                        <button className="sm-icon-btn" title="Modifier"
                          onClick={() => { setEditTarget(ent); setEditForm({ nom:ent.nom||'', ninea:ent.ninea||'', rc:ent.rc||'', telephone:ent.telephone||'', adresse:ent.adresse||'' }); }}>
                          <Edit2 size={13}/>
                        </button>
                        <button className="sm-icon-btn sm-icon-btn--danger" title="Supprimer"
                          onClick={() => handleDelete(ent)}>
                          <Trash2 size={13}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modal Renouvellement ── */}
      {renewalTarget && (
        <Modal
          title="Renouveler l'abonnement"
          subtitle={renewalTarget.nom}
          onClose={() => setRenewalTarget(null)}
        >
          <div className="sm-modal-info">
            <span><strong>Plan :</strong> {renewalTarget.abonnement?.plan}</span>
            <span><Calendar size={13}/> Fin actuelle : {moment(renewalTarget.abonnement?.dateFin).format('D MMM YYYY')}</span>
          </div>
          <form onSubmit={handleRenewal}>
            <div className="sm-form-group">
              <label>Mois supplémentaires *</label>
              <input type="number" min="1" max="24"
                value={renewalForm.moisSupplementaires}
                onChange={e => setRenewalForm({...renewalForm, moisSupplementaires: +e.target.value})}
                required/>
              <p className="sm-form-hint">
                Nouvelle date de fin : {moment(renewalTarget.abonnement?.dateFin).add(renewalForm.moisSupplementaires, 'months').format('D MMMM YYYY')}
              </p>
            </div>
            <div className="sm-form-group">
              <label>Raison *</label>
              <textarea rows="2" value={renewalForm.raison}
                onChange={e => setRenewalForm({...renewalForm, raison: e.target.value})}
                placeholder="Ex: Geste commercial, paiement différé…" required/>
            </div>
            <div className="sm-alert sm-alert--info">
              <AlertCircle size={14}/> Prolongation sans paiement immédiat.
            </div>
            <div className="sm-modal-footer">
              <button type="button" className="sm-btn-cancel" onClick={() => setRenewalTarget(null)}>Annuler</button>
              <button type="submit" className="sm-btn-confirm" disabled={submitting}>
                {submitting ? <Loader size={14} className="sm-spin"/> : <><Check size={14}/> Confirmer</>}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Modal Changement Plan ── */}
      {planTarget && (
        <Modal
          title="Changer le plan"
          subtitle={planTarget.nom}
          onClose={() => setPlanTarget(null)}
        >
          <div className="sm-modal-info">
            <span><strong>Plan actuel :</strong> {planTarget.abonnement?.plan}</span>
          </div>
          <form onSubmit={handlePlanChange}>
            <div className="sm-form-group">
              <label>Nouveau plan *</label>
              <div className="sm-plan-options">
                {[
                  { id:'starter',    label:'Starter',    price:'100 000 XOF', desc:'1 boutique, 2 users' },
                  { id:'pro',        label:'Pro',        price:'150 000 XOF', desc:'3 boutiques, 5 users' },
                  { id:'entreprise', label:'Entreprise', price:'300 000 XOF', desc:'Illimité' },
                ].map(p => {
                  const pl = PLAN_COLORS[p.id];
                  const selected = planForm.nouveauPlan === p.id;
                  return (
                    <label key={p.id} className={`sm-plan-option ${selected ? 'selected' : ''}`}
                      style={{ borderColor: selected ? pl.color : '#e2e8f0' }}>
                      <input type="radio" name="plan" value={p.id}
                        checked={selected} onChange={() => setPlanForm({...planForm, nouveauPlan: p.id})}
                        style={{ display:'none' }}/>
                      <div className="sm-plan-opt-name" style={{ color: pl.color }}>{p.label}</div>
                      <div className="sm-plan-opt-price">{p.price}/mois</div>
                      <div className="sm-plan-opt-desc">{p.desc}</div>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="sm-form-group">
              <label>Raison *</label>
              <textarea rows="2" value={planForm.raison}
                onChange={e => setPlanForm({...planForm, raison: e.target.value})}
                placeholder="Ex: Upgrade demandé, downgrade…" required/>
            </div>
            <div className="sm-alert sm-alert--warning">
              <AlertCircle size={14}/> Le changement prend effet immédiatement.
            </div>
            <div className="sm-modal-footer">
              <button type="button" className="sm-btn-cancel" onClick={() => setPlanTarget(null)}>Annuler</button>
              <button type="submit" className="sm-btn-confirm" disabled={submitting}>
                {submitting ? <Loader size={14} className="sm-spin"/> : <><Check size={14}/> Appliquer</>}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Modal Ajout ── */}
      {showAddModal && (
        <Modal title="Nouvelle entreprise" onClose={() => setShowAddModal(false)}>
          <form onSubmit={handleAdd}>
            <div className="sm-form-row">
              <div className="sm-form-group">
                <label>Nom de l'entreprise *</label>
                <input type="text" value={addForm.nom}
                  onChange={e => setAddForm({...addForm, nom: e.target.value})}
                  placeholder="Ex: Boutique Alpha" required/>
              </div>
              <div className="sm-form-group">
                <label>Email *</label>
                <input type="email" value={addForm.email}
                  onChange={e => setAddForm({...addForm, email: e.target.value})}
                  placeholder="contact@entreprise.com" required/>
              </div>
            </div>
            <div className="sm-form-row">
              <div className="sm-form-group">
                <label>Mot de passe *</label>
                <input type="password" value={addForm.password}
                  onChange={e => setAddForm({...addForm, password: e.target.value})}
                  placeholder="Min. 6 caractères" required minLength="6"/>
              </div>
              <div className="sm-form-group">
                <label>Plan d'abonnement *</label>
                <select value={addForm.plan} onChange={e => setAddForm({...addForm, plan: e.target.value})}>
                  <option value="starter">Starter — 100 000 XOF</option>
                  <option value="pro">Pro — 150 000 XOF</option>
                  <option value="entreprise">Entreprise — 300 000 XOF</option>
                </select>
              </div>
            </div>
            <div className="sm-form-row">
              <div className="sm-form-group">
                <label>Prénom administrateur</label>
                <input type="text" value={addForm.prenom}
                  onChange={e => setAddForm({...addForm, prenom: e.target.value})} placeholder="Admin"/>
              </div>
              <div className="sm-form-group">
                <label>Nom administrateur</label>
                <input type="text" value={addForm.nomAdmin}
                  onChange={e => setAddForm({...addForm, nomAdmin: e.target.value})} placeholder="Principal"/>
              </div>
            </div>
            <div className="sm-alert sm-alert--info">
              <AlertCircle size={14}/> Un compte administrateur sera créé automatiquement.
            </div>
            <div className="sm-modal-footer">
              <button type="button" className="sm-btn-cancel" onClick={() => setShowAddModal(false)}>Annuler</button>
              <button type="submit" className="sm-btn-confirm" disabled={submitting}>
                {submitting ? <Loader size={14} className="sm-spin"/> : <><Plus size={14}/> Créer</>}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Modal Modification ── */}
      {editTarget && (
        <Modal title="Modifier l'entreprise" subtitle={editTarget.nom} onClose={() => setEditTarget(null)}>
          <form onSubmit={handleEdit}>
            <div className="sm-form-group">
              <label>Nom de l'entreprise *</label>
              <input type="text" value={editForm.nom}
                onChange={e => setEditForm({...editForm, nom: e.target.value})} required/>
            </div>
            <div className="sm-form-row">
              <div className="sm-form-group">
                <label>NINEA</label>
                <input type="text" value={editForm.ninea}
                  onChange={e => setEditForm({...editForm, ninea: e.target.value})} placeholder="Ex: 001234567 2Z3"/>
              </div>
              <div className="sm-form-group">
                <label>RC</label>
                <input type="text" value={editForm.rc}
                  onChange={e => setEditForm({...editForm, rc: e.target.value})} placeholder="Registre de commerce"/>
              </div>
            </div>
            <div className="sm-form-row">
              <div className="sm-form-group">
                <label>Téléphone</label>
                <input type="tel" value={editForm.telephone}
                  onChange={e => setEditForm({...editForm, telephone: e.target.value})} placeholder="+221 77 000 00 00"/>
              </div>
              <div className="sm-form-group">
                <label>Adresse</label>
                <input type="text" value={editForm.adresse}
                  onChange={e => setEditForm({...editForm, adresse: e.target.value})} placeholder="Adresse complète"/>
              </div>
            </div>
            <div className="sm-modal-footer">
              <button type="button" className="sm-btn-cancel" onClick={() => setEditTarget(null)}>Annuler</button>
              <button type="submit" className="sm-btn-confirm" disabled={submitting}>
                {submitting ? <Loader size={14} className="sm-spin"/> : <><Check size={14}/> Enregistrer</>}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
